/**
 * Canonical catalog for Create Request / Request Detail navigation steps (#997).
 * Single source of truth for base vs optional focus areas and nav ordering.
 */

export type RequestFormStepInclusion = 'base' | 'optional';

export interface RequestFormStepCatalogEntry {
  id: string;
  /** Sidebar / nav label */
  title: string;
  /** Add Scope picker label (defaults to title) */
  pickerLabel?: string;
  navOrder: number;
  inclusion: RequestFormStepInclusion;
  /** Add Scope picker — `coming-soon` rows are visible but not addable (#997). */
  scopePickerAvailability?: 'live' | 'coming-soon';
}

/** Always present on new requests (Site Walk lives on Opportunity — no separate step). */
export const BASE_REQUEST_FORM_STEP_IDS = [
  'opportunity',
  'documents',
  'summary',
] as const;

export type BaseRequestFormStepId = (typeof BASE_REQUEST_FORM_STEP_IDS)[number];

/** Optional focus areas the user can add via Add Scope (#997, #1330). */
export const OPTIONAL_REQUEST_FORM_STEP_IDS = [
  'site-information',
  'facility-power',
  'large-ups',
  'power-distribution',
  'rack-power',
  'small-ups',
  'cooling-readiness',
  'liquid-cooling',
] as const;

/** Pre-#1330 optional steps for legacy requests (`enabled_step_ids` NULL). */
export const LEGACY_OPTIONAL_REQUEST_FORM_STEP_IDS = [
  'site-information',
  'facility-power',
  'rack-power',
  'small-ups',
  'cooling-readiness',
  'liquid-cooling',
] as const;

export type OptionalRequestFormStepId =
  (typeof OPTIONAL_REQUEST_FORM_STEP_IDS)[number];

export type RequestFormStepId =
  | BaseRequestFormStepId
  | OptionalRequestFormStepId;

export const REQUEST_FORM_STEP_REGISTRY: readonly RequestFormStepCatalogEntry[] =
  [
    {
      id: 'opportunity',
      title: 'Opportunity',
      pickerLabel: 'Opportunity Information',
      navOrder: 10,
      inclusion: 'base',
    },
    {
      id: 'site-information',
      title: 'Site Information',
      navOrder: 20,
      inclusion: 'optional',
      scopePickerAvailability: 'live',
    },
    {
      id: 'facility-power',
      title: 'Facility Power Readiness',
      pickerLabel: 'Facility Power',
      navOrder: 30,
      inclusion: 'optional',
      scopePickerAvailability: 'live',
    },
    {
      id: 'large-ups',
      title: 'Large UPS',
      navOrder: 35,
      inclusion: 'optional',
      scopePickerAvailability: 'live',
    },
    {
      id: 'power-distribution',
      title: 'Power Distribution',
      navOrder: 36,
      inclusion: 'optional',
      scopePickerAvailability: 'live',
    },
    {
      id: 'rack-power',
      title: 'Racks',
      navOrder: 40,
      inclusion: 'optional',
      scopePickerAvailability: 'live',
    },
    {
      id: 'small-ups',
      title: 'Small UPS',
      navOrder: 50,
      inclusion: 'optional',
      scopePickerAvailability: 'live',
    },
    {
      id: 'cooling-readiness',
      title: 'Cooling',
      navOrder: 60,
      inclusion: 'optional',
      scopePickerAvailability: 'live',
    },
    {
      id: 'liquid-cooling',
      title: 'Liquid Cooling',
      pickerLabel: 'Liquid Cooling',
      navOrder: 70,
      inclusion: 'optional',
      scopePickerAvailability: 'live',
    },
    {
      id: 'documents',
      title: 'Supporting Documents',
      navOrder: 80,
      inclusion: 'base',
    },
    {
      id: 'summary',
      title: 'Summary',
      navOrder: 90,
      inclusion: 'base',
    },
  ] as const;

const registryById = new Map(
  REQUEST_FORM_STEP_REGISTRY.map(entry => [entry.id, entry]),
);

export function getRequestFormStepCatalogEntry(
  stepId: string,
): RequestFormStepCatalogEntry | undefined {
  return registryById.get(stepId);
}

export function getOptionalScopePickerCatalogEntries(): RequestFormStepCatalogEntry[] {
  return REQUEST_FORM_STEP_REGISTRY.filter(
    entry => entry.inclusion === 'optional',
  );
}

export function isScopePickerComingSoon(stepId: string): boolean {
  return (
    getRequestFormStepCatalogEntry(stepId)?.scopePickerAvailability ===
    'coming-soon'
  );
}

export function getScopeStepPickerLabel(stepId: string): string {
  const entry = getRequestFormStepCatalogEntry(stepId);
  return entry?.pickerLabel ?? entry?.title ?? stepId;
}

export function isOptionalRequestFormStepId(
  stepId: string,
): stepId is OptionalRequestFormStepId {
  return (OPTIONAL_REQUEST_FORM_STEP_IDS as readonly string[]).includes(stepId);
}

export function isBaseRequestFormStepId(
  stepId: string,
): stepId is BaseRequestFormStepId {
  return (BASE_REQUEST_FORM_STEP_IDS as readonly string[]).includes(stepId);
}

/** Default enabled set persisted for brand-new requests. */
export function getDefaultEnabledStepIdsForNewRequest(): RequestFormStepId[] {
  return [...BASE_REQUEST_FORM_STEP_IDS];
}

/** Legacy requests (`enabled_step_ids` NULL): optional steps before #1330 child scopes. */
export function getLegacyFullEnabledStepIds(): RequestFormStepId[] {
  return [
    'opportunity',
    ...LEGACY_OPTIONAL_REQUEST_FORM_STEP_IDS,
    'documents',
    'summary',
  ];
}

export function sortStepIdsByRegistry(ids: readonly string[]): string[] {
  const order = new Map(
    REQUEST_FORM_STEP_REGISTRY.map(entry => [entry.id, entry.navOrder]),
  );
  return [...ids].sort((a, b) => (order.get(a) ?? 999) - (order.get(b) ?? 999));
}

/** Summary accordion keys → form step id (#997 Phase C, #1330). */
export const SUMMARY_SECTION_TO_STEP_ID: Record<string, RequestFormStepId> = {
  opportunity: 'opportunity',
  siteDimensions: 'site-information',
  racks: 'rack-power',
  facilityPower: 'facility-power',
  largeUps: 'large-ups',
  powerDistribution: 'power-distribution',
  rackPower: 'rack-power',
  smallUps: 'small-ups',
  coolingReadiness: 'cooling-readiness',
  liquidCooling: 'liquid-cooling',
  supportingDocuments: 'documents',
};

function resolveEnabledStepIdsForSummary(
  stored: string[] | null | undefined,
): RequestFormStepId[] {
  if (stored == null) {
    return getLegacyFullEnabledStepIds();
  }
  if (stored.length === 0) {
    return getDefaultEnabledStepIdsForNewRequest();
  }

  const seen = new Set<string>();
  const merged: string[] = [];
  for (const id of sortStepIdsByRegistry(stored)) {
    if (getRequestFormStepCatalogEntry(id) && !seen.has(id)) {
      seen.add(id);
      merged.push(id);
    }
  }
  for (const baseId of BASE_REQUEST_FORM_STEP_IDS) {
    if (!seen.has(baseId)) {
      merged.push(baseId);
    }
  }
  return sortStepIdsByRegistry(merged) as RequestFormStepId[];
}

export function isSummarySectionEnabled(
  summarySectionKey: string,
  storedEnabledStepIds: string[] | null | undefined,
): boolean {
  const stepId = SUMMARY_SECTION_TO_STEP_ID[summarySectionKey];
  if (!stepId) {
    return true;
  }
  return resolveEnabledStepIdsForSummary(storedEnabledStepIds).includes(stepId);
}
